/**
 * The help centre. Sections are anchored because the footer links straight at
 * them — `/support#billing` has to land on billing, not on the top of a page
 * the reader then has to search.
 */

import { Link } from 'react-router-dom';
import { DocSection, PublicPage } from './PublicPage.js';

const TOC: [string, string][] = [
  ['Resource grid', '#grid'],
  ['Student portal', '#portal'],
  ['Maintenance', '#maintenance'],
  ['Billing', '#billing'],
  ['Accounts and access', '#accounts'],
  ['Getting your data out', '#export'],
];

export function Support(): JSX.Element {
  return (
    <PublicPage
      title="Help centre"
      lede={<>
        How the parts of FlightSlots work, and what to do when one of them does not.
        If none of this covers it, <Link to="/contact?about=support">write to us</Link>.
      </>}
    >
      <div className="lp__toc">
        {TOC.map(([label, href]) => <a key={href} href={href}>{label}</a>)}
      </div>

      <DocSection id="grid" title="Resource grid">
        <p>
          The grid puts aircraft and instructors on rows and the hours across. A reservation is a
          block on both: move it and the student, the instructor and the invoice line move with it.
        </p>
        <p>
          The board shows the day; it does not change it. Open a block and the reservation opens
          with it, and what happens next — confirm, authorize, cancel, close out — is decided
          there, where you can see what you are agreeing to. Overlapping bookings stack into
          sub-lanes rather than hiding each other, so a double booking is visible rather than
          discovered on the ramp.
        </p>
        <p>
          <strong>Some changes will be refused.</strong> Something failed a check — the aircraft is
          grounded, the instructor is outside their duty window, or the student is not signed off
          for it. The finding says which, with the rule it came from.
        </p>
      </DocSection>

      <DocSection id="portal" title="Student portal">
        <p>
          Students see their own lessons, their syllabus position, their documents and their
          balance. They can request a slot, and the portal only ever offers times that already pass
          the checks — so a request is not a negotiation about whether the flight is possible.
        </p>
        <p>
          A request holds the slot for twelve hours while an instructor confirms it. After that it
          lapses and the time goes back on the board.
        </p>
        <p>
          <strong>A student cannot see a time you expect them to.</strong> Usually the aircraft is
          booked, the syllabus gate has not opened under Part 141, or an endorsement has expired.
          Their record shows which.
        </p>
      </DocSection>

      <DocSection id="maintenance" title="Maintenance and squawks">
        <p>
          Filing a squawk records the defect and its operational effect — anything from day-VFR-only
          to grounded. Grounding is one action: every future reservation on that airframe surfaces
          immediately, with a message ready to send to each affected student.
        </p>
        <p>
          Inspections are tracked against Hobbs. The grid projects hours remaining at current
          utilisation, so a 100-hour that lands mid-week is visible the week before rather than the
          morning of.
        </p>
        <p>
          Clearing a squawk returns the aircraft to service from that moment. Reservations that were
          moved off it are not moved back automatically — that is a person's call.
        </p>
      </DocSection>

      <DocSection id="billing" title="Billing">
        <p>
          Block out and block in on the ramp become the invoice. Aircraft time bills wet at Hobbs,
          instruction bills in tenths, and neither is re-entered anywhere.
        </p>
        <p>
          Rates live on the aircraft and on the instructor, so a rental, a dual lesson and a
          checkride on the same airframe price differently without anyone choosing a rate at the
          desk.
        </p>
        <p>
          <strong>An invoice looks wrong.</strong> Check the Hobbs entries on the reservation
          first — the invoice is derived from them, so correcting the flight corrects the invoice.
        </p>
      </DocSection>

      <DocSection id="accounts" title="Accounts and access">
        <p>
          There are four roles: management, instructor, dispatch and student. What each can see
          follows how the school is certificated rather than a settings page — a freelance CFI has
          no dispatch desk to grant, and a Part 61 school has no stage gates to enforce.
        </p>
        <p>
          Students and instructors join with a code from the school. Dispatch is added by
          management, deliberately: someone who moves aircraft for the whole school should not be
          able to add themselves.
        </p>
        <p>
          <strong>You cannot sign in.</strong> Sign-in is limited to ten attempts in fifteen minutes
          per address, so wait a few minutes before trying again. If it still fails,{' '}
          <Link to="/contact?about=support">tell us</Link> and say which email you used.
        </p>
      </DocSection>

      <DocSection id="export" title="Getting your data out">
        <p>
          Training records, stage records and invoices export in full, with the regulation applied to
          each finding. Your records are yours, and leaving is not a support ticket.
        </p>
        <p>
          The database is a single SQLite file. If you self-host, that file is the whole school and
          you already have it. If we host you, ask through the{' '}
          <Link to="/contact?about=privacy">privacy and records</Link> form and we will send it.
        </p>
        <p>
          What FlightSlots holds and how long it holds it is set out in the{' '}
          <Link to="/privacy">privacy statement</Link>.
        </p>
      </DocSection>

      <DocSection title="Still stuck?">
        <p>
          <Link to="/status">Check system status</Link> to rule us out, then{' '}
          <Link to="/contact?about=support">send us the details</Link> — what you did, what happened,
          and the tail number or the student if it involves one.
        </p>
      </DocSection>
    </PublicPage>
  );
}
